import { useEffect, useMemo } from 'react';
import { useLocation, Outlet, useNavigate } from 'react-router';
import {
    LayoutGrid01,
    Package,
    Users01,
    BookOpen01,
    Settings01,
    HelpCircle,
    Bell01,
    ShoppingCart01,
    FileCheck02,
    File01,
    Globe02,
    AlertCircle
} from '@untitledui/icons';
import { SidebarNavigationSimple } from '@/components/application/app-navigation/sidebar-navigation/sidebar-simple';
import type { NavItemType, NavSubItemType } from '@/components/application/app-navigation/config';
import { useTranslation } from 'react-i18next';
import { useMenuConfiguration } from '@/hooks/useMenuConfiguration';
import { MenuRouteGuard } from '@/components/application/route-guard/menu-route-guard';
import { ShipperOnboardingProvider } from '@/components/application/onboarding/shipper-onboarding-context';
import { useActivityUnreadCount } from '@/hooks/api/use-activities';
import { useHasPendingReviews } from '@/hooks/api/use-review-center';
import { ClipboardCheck } from '@untitledui/icons';
import { useChatAI } from '@/components/application/ai-chat/chat-ai-context';
import { AskEveFab } from '@/components/application/ai-chat/ask-eve-fab';
import { AskEveSidePanel } from '@/components/application/ai-chat/ask-eve-side-panel';

/** Top-level or group node with optional menu key (leaf) / children */
type NavTreeItem = NavItemType & { key?: string };

/**
 * Drops hidden menu entries; for groups keeps only visible children and sets
 * parent `href` to the first child (sidebar collapsed icon-only link).
 */
function filterShipperNavItems(
    items: NavTreeItem[],
    isEnabled: (key: string | undefined) => boolean
): NavItemType[] {
    const out: NavItemType[] = [];
    for (const item of items) {
        if (item.items?.length) {
            const children: NavSubItemType[] = [];
            for (const c of item.items) {
                if (!c.key || isEnabled(c.key)) {
                    children.push(c);
                }
            }
            if (children.length === 0) continue;
            out.push({
                ...item,
                href: children[0].href,
                items: children,
            });
            continue;
        }
        if (!item.key || isEnabled(item.key)) {
            out.push(item);
        }
    }
    return out;
}

export const ShipperLayout = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const currentPath = location.pathname;
    const { t } = useTranslation('navigation');
    const { isMenuItemEnabled } = useMenuConfiguration();
    const { openAskEvePanel, closeAskEvePanel, isAskEvePanelOpen } = useChatAI();
    const askEveEnabled = isMenuItemEnabled('ask-eve');

    const { data: unreadCount = 0 } = useActivityUnreadCount('shipper');
    const { data: pendingReviewsData } = useHasPendingReviews();

    const allSidebarItems: NavTreeItem[] = useMemo(() => {
        const activityBadge =
            unreadCount > 0 ? (
                <span className="absolute -right-0.5 -top-0.5 flex h-2 w-2 items-center justify-center rounded-full bg-error-600 ring-1 ring-primary">
                    <span className="sr-only">{t('unreadNotifications', { count: unreadCount })}</span>
                </span>
            ) : undefined;

        const reviewBadge = pendingReviewsData?.has_pending ? (
            <span className="absolute -right-0.5 -top-0.5 flex h-2 w-2 items-center justify-center rounded-full bg-error-600 ring-1 ring-primary" />
        ) : undefined;

        return [
            {
                key: 'controlHub',
                label: t('controlHub'),
                href: '/shipper/control-hub',
                icon: LayoutGrid01,
                iconSrc: '/icons/ICO-control-hub.svg',
                iconHoverSrc: '/icons/ICO-control-hub-hover.svg',
            },
            {
                label: t('shipperGroup.operations'),
                href: '/shipper/containers',
                icon: Package,
                items: [
                    {
                        key: 'containers',
                        label: t('containers'),
                        href: '/shipper/containers',
                        icon: Package,
                        iconSrc: '/icons/ICO-container.svg',
                        iconHoverSrc: '/icons/ICO-container-hover.svg',
                    },
                    {
                        key: 'liveTracking',
                        label: t('liveTracking'),
                        href: '/shipper/live-tracking',
                        icon: Globe02,
                    },
                    {
                        key: 'documents',
                        label: t('eveDrive'),
                        href: '/shipper/documents',
                        icon: File01,
                    },
                ],
            },
            {
                label: t('shipperGroup.coordination'),
                href: '/shipper/activity',
                icon: Bell01,
                items: [
                    {
                        key: 'activity',
                        label: t('activity'),
                        href: '/shipper/activity',
                        icon: Bell01,
                        badge: activityBadge,
                    },
                    {
                        key: 'issues',
                        label: t('issues'),
                        href: '/shipper/issues',
                        icon: AlertCircle,
                    },
                    {
                        key: 'reviewCenter',
                        label: t('reviewCenter'),
                        href: '/shipper/review-center',
                        icon: ClipboardCheck,
                        badge: reviewBadge,
                    },
                ],
            },
            {
                label: t('shipperGroup.commercial'),
                href: '/shipper/orders',
                icon: ShoppingCart01,
                items: [
                    {
                        key: 'orders',
                        label: t('orders'),
                        href: '/shipper/orders',
                        icon: ShoppingCart01,
                    },
                    {
                        key: 'quotes',
                        label: t('quotes'),
                        href: '/shipper/quotes',
                        icon: FileCheck02,
                    },
                ],
            },
            {
                label: t('shipperGroup.network'),
                href: '/shipper/business-partners',
                icon: Users01,
                items: [
                    {
                        key: 'businessPartners',
                        label: t('businessPartners'),
                        href: '/shipper/business-partners',
                        icon: Users01,
                    },
                    {
                        key: 'addressBook',
                        label: t('addressBook'),
                        href: '/shipper/address-book',
                        icon: BookOpen01,
                    },
                ],
            },
            {
                key: 'settings',
                label: t('settings'),
                href: '/shipper/profile',
                icon: Settings01,
            },
        ];
    }, [t, unreadCount, pendingReviewsData?.has_pending]);

    const sidebarItems = useMemo(
        () => filterShipperNavItems(allSidebarItems, (key) => !key || isMenuItemEnabled(key)),
        [allSidebarItems, isMenuItemEnabled]
    );

    const footerItems: NavItemType[] = [
        {
            label: t('support'),
            href: '#',
            icon: HelpCircle,
        },
    ];

    const env = import.meta.env.VITE_ENV;
    const hasEnvironmentBanner = env !== 'production';

    const isOnLiveTrackingPage = currentPath === '/shipper/live-tracking';
    const isOnFullWidthContentPage =
        currentPath === '/shipper/containers' ||
        currentPath.startsWith('/shipper/containers/') ||
        currentPath === '/shipper/control-hub' ||
        currentPath === '/shipper/documents' ||
        currentPath.startsWith('/shipper/documents/') ||
        currentPath === '/shipper/issues' ||
        currentPath.startsWith('/shipper/issues/') ||
        currentPath === '/shipper/business-partners' ||
        currentPath.startsWith('/shipper/business-partners/') ||
        currentPath === '/shipper/address-book' ||
        currentPath.startsWith('/shipper/address-book/') ||
        currentPath === '/shipper/profile' ||
        currentPath.startsWith('/shipper/profile/') ||
        currentPath === '/shipper/activity' ||
        currentPath === '/shipper/review-center';

    const isFullPage = isOnLiveTrackingPage;

    useEffect(() => {
        if (!askEveEnabled && isAskEvePanelOpen) {
            closeAskEvePanel();
        }
    }, [askEveEnabled, isAskEvePanelOpen, closeAskEvePanel]);

    useEffect(() => {
        const st = location.state as { openAskEve?: boolean } | null;
        if (st?.openAskEve) {
            openAskEvePanel();
            navigate(
                { pathname: location.pathname, search: location.search, hash: location.hash },
                { replace: true, state: {} }
            );
        }
    }, [location.pathname, location.state, location.search, location.hash, navigate, openAskEvePanel]);

    return (
        <div className={`flex h-screen min-w-0 overflow-x-hidden bg-secondary ${hasEnvironmentBanner ? 'pt-7' : ''}`}>
            <SidebarNavigationSimple
                activeUrl={currentPath}
                items={sidebarItems}
                footerItems={footerItems}
                showAccountCard={true}
                className="hidden lg:flex"
            />

            <ShipperOnboardingProvider>
                <div className="relative flex min-h-0 min-w-0 flex-1 flex-col">
                    <div className="relative flex min-h-0 min-w-0 flex-1">
                        <main
                            className={`relative z-0 min-h-0 min-w-0 flex-1 bg-secondary ${isFullPage ? 'overflow-hidden' : 'overflow-y-auto overflow-x-hidden'}`}
                        >
                            <div
                                className={
                                    isFullPage
                                        ? 'h-full min-h-0 py-0'
                                        : isOnFullWidthContentPage
                                          ? 'mx-auto w-full min-w-0 max-w-none px-3 py-6 sm:px-4 lg:px-5'
                                          : 'mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-6'
                                }
                            >
                                <MenuRouteGuard>
                                    <Outlet />
                                </MenuRouteGuard>
                            </div>
                        </main>
                        {askEveEnabled && isAskEvePanelOpen ? (
                            <AskEveSidePanel variant="shipper" onClose={closeAskEvePanel} />
                        ) : null}
                        {askEveEnabled ? <AskEveFab /> : null}
                    </div>
                </div>
            </ShipperOnboardingProvider>
        </div>
    );
};
